import { FC, useEffect, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { usePermission } from 'hooks';
import { Messages } from 'constants/rpcTypes';
import api from 'api/user';
import { RootState } from 'store';
import checkFilters from 'helpers/checkFilters';
import MenuContext from 'components/ui/menuContext';
import Icon from 'components/ui/icon';
import { updateLanguage } from 'actions/user';
import { WL_ID_19 } from 'constants/wlIds';
import './changeLang.scss';
import { ComponentRegistry } from 'runtime/registry/components/ComponentRegistry';

interface ChangeLangProps {
  wlId?: number | null;
}

export const ChangeLang: FC<ChangeLangProps> = ({ wlId }) => {
  const dispatch = useDispatch();
  const { isEnabled } = usePermission();
  const isWl19 = wlId === WL_ID_19;

  const interfaceLang = useSelector(
    (state: RootState) => state.localization.interfaceLang
  );
  const languagesAvailable =
    useSelector(
      (state: RootState) => state.filtersValues?.interfaceLanguage?.list
    ) || [];

  useEffect(() => {
    checkFilters(['interfaceLanguage']);
  }, []);

  const handleChangeLang = async ({ id: newLang }: { id: string }) => {
    if (newLang === interfaceLang) {
      return;
    }

    if (newLang === 'xx') {
      dispatch(
        updateLanguage({
          interfaceLang: newLang,
        })
      );
      return;
    }

    try {
      await api.updateUserLanguage(newLang);
    } catch (err) {
      console.error(err);
    }
  };

  const languages: {
    id: string;
    text: string;
    countryCode?: string;
  }[] = useMemo(() => {
    if (isEnabled(Messages.Localize_LexemesList) && languagesAvailable.length) {
      return [...languagesAvailable, { id: 'xx', text: 'Keys' }];
    }

    return languagesAvailable;
  }, [languagesAvailable]);

  const currentLang = languages.find(({ id }) => id === interfaceLang);

  if (!languages.length) {
    return null;
  }

  const list = languages.map(({ id, text, countryCode }) => ({
    id,
    active: id === interfaceLang,
    content: (
      <LanguageItem
        key={id}
        text={text}
        countryCode={countryCode}
        isActive={id === interfaceLang}
        isWl19={isWl19}
      />
    ),
    onClick: handleChangeLang,
  }));

  const props = ComponentRegistry.getProps('ChangeLang')

  return (
    <MenuContext
      name={classNames('lang', { 'lang-wl19': isWl19 })}
      customClass={classNames('change-lang', { 'change-lang-wl19': isWl19 })}
      panelCustomClass={props?.panelCustomClass}
      list={list}
      value={{
        buttonContent: isWl19 ? (
          <div className='change-lang-wl19__control'>
            <FlagOrFallback countryCode={currentLang?.countryCode} size={20} />
            <Icon
              name='etoChevronDown'
              size={16}
              className='change-lang-wl19__chevron'
            />
          </div>
        ) : (
          <div className='change-lang__item change-lang__item_btn'>
            <span className='change-lang__text'>{currentLang?.text}</span>
            <Icon
              name='im-Arrow-down'
              size={10}
              className='change-lang__arrow-icon'
            />
          </div>
        ),
      }}
    />
  );
};

const LanguageItem: FC<{
  text: string;
  isActive: boolean;
  countryCode?: string;
  isWl19?: boolean;
}> = ({ text, countryCode, isActive, isWl19 }) => {
  if (isWl19) {
    return (
      <div
        className={classNames('change-lang-wl19__option', {
          'change-lang-wl19__option--active': isActive,
        })}>
        <FlagOrFallback countryCode={countryCode} size={20} />
        <span className='change-lang-wl19__option-text'>{text}</span>
        {isActive && (
          <Icon name='etoCheck' size={16} className='change-lang-wl19__check' />
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames('change-lang__item', {
        'change-lang_active': isActive,
      })}>
      <div className='change-lang__info'>
        {countryCode ? (
          <ReactCountryFlag
            countryCode={countryCode}
            svg
            style={{
              width: 16,
              height: 16,
              borderRadius: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Icon name='im-Info' size={16} className='change-lang__info-icon' />
        )}
        <span className='change-lang__text'>{text}</span>
      </div>
      <Icon
        name='im-Check-mark'
        size={10}
        className='change-lang__check-icon'
      />
    </div>
  );
};

const FlagOrFallback: FC<{ countryCode?: string; size: number }> = ({
  countryCode,
  size,
}) => {
  if (countryCode) {
    return (
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <Icon
      name='im-Info'
      size={size}
      className='change-lang-wl19__fallback-icon'
    />
  );
};
